import { yupResolver } from "@hookform/resolvers/yup";
import {
	Button,
	FormControl,
	FormControlText,
	InputText,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Modalv2
} from "@itandi/itandi-bb-ui";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import type { Deal } from "~/models/Deal";
import type {
	DealVendorNote,
	DealVendorNoteParam,
	DealVendorNoteTemplate
} from "~/models/DealVendorNote";
import {
	buildDealVendorNoteParam,
	validationSchema
} from "~/models/DealVendorNote";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/vendors/deals/DealVendorNote";

type Props = {
	deal: Deal;
	dealVendorNote: DealVendorNote | null;
	dealVendorNoteTemplate: DealVendorNoteTemplate;
	handleClose: () => void;
	handleMutate: () => Promise<void>;
	isOpen: boolean;
};

export const DealVendorNoteModal: React.FC<Props> = ({
	deal,
	dealVendorNote,
	dealVendorNoteTemplate,
	isOpen,
	handleClose,
	handleMutate
}: Props) => {
	const {
		register,
		handleSubmit,
		formState,
		setValue,
		formState: { errors }
	} = useForm<DealVendorNoteParam>({
		defaultValues: dealVendorNote
			? buildDealVendorNoteParam(dealVendorNote)
			: {
					note: dealVendorNoteTemplate.dealVendorNoteTemplate || "",
					version: null
				},
		resolver: yupResolver(validationSchema)
	});
	const createMode = dealVendorNote === null;
	const handleModalSubmit = React.useCallback(() => {
		handleSubmit(async (submitValue: DealVendorNoteParam) => {
			const response = createMode
				? await create(submitValue, deal.id)
				: await update(submitValue, dealVendorNote.id, deal.id);
			if (isSuccess(response)) {
				await handleMutate();
				handleClose();
				if (createMode) {
					toast.success("作成しました");
				} else {
					toast.success("更新しました");
					// versionを1つ進める
					setValue("version", (submitValue.version ?? 0) + 1);
				}
				return;
			}
			toast.error(`${response.message}`);
		})();
	}, [
		handleSubmit,
		createMode,
		deal.id,
		dealVendorNote,
		handleClose,
		handleMutate,
		setValue
	]);

	return (
		<Modalv2 isOpen={isOpen} onClose={handleClose} size="l">
			<ModalHeader
				description="施工会社/実施会社双方から編集可能です。管理会社へ共有されます。"
				title="案件メモの登録"
			/>
			<ModalBody>
				<FormControl
					isInvalid={!!errors.note}
					label="案件メモ"
					variant="vertical"
				>
					<InputText
						isInvalid={!!errors.note}
						maxRows={20}
						placeholder="共有したいメモを記載してください"
						resize="autoRows"
						rows={8}
						{...register("note")}
					/>
					{createMode && dealVendorNoteTemplate.dealVendorNoteTemplate ? (
						<FormControlText help="テンプレートが適応されました。" />
					) : null}
					<FormControlText help="改行、URLはマークダウンで記載可能です。改行は半角スペース2つ、URLは[リンク名](URL)で記載可能です" />
					{errors.note ? (
						<FormControlText error={errors.note.message || ""} />
					) : null}
				</FormControl>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} variant="secondary">
						保存せず閉じる
					</Button>
				}
				rightArea={
					<Button
						disabled={formState.isSubmitting}
						onClick={handleModalSubmit}
						variant="primary"
					>
						{createMode ? "作成" : "更新"}
					</Button>
				}
			/>
		</Modalv2>
	);
};
